<script setup lang="ts">
/**
 * DEV-LOCAL Singpass 로그인 다이얼로그 (로컬 Python 스크래핑 경유).
 *
 * "Choose a login method" — QR 스캔 또는 비밀번호+OTP. 인증 완료 시
 * { sessionId, mstIdx, filingAgentUEN } 을 emit 한다. (실제 자격증명은 사용자가 입력)
 */
import { ref, watch, onBeforeUnmount } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { sgPyScrapingApi } from './composables/sgPyScrapingApi';

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    taskId?: string;
    filingAgentUEN?: string;
  }>(),
  { taskId: '', filingAgentUEN: '' },
);

const emit = defineEmits<{
  'update:modelValue': [v: boolean];
  authenticated: [payload: { sessionId: string; mstIdx: number | null; filingAgentUEN: string }];
  error: [message: string];
}>();

type Step =
  | 'understand'
  | 'method'
  | 'loading'
  | 'qr'
  | 'qr_expired'    // Singpass QR TTL 초과 → Refresh 버튼 노출
  | 'password'
  | 'otp'
  | 'authenticating'
  | 'success';
const step = ref<Step>('understand');
const errorMsg = ref('');

const mstIdx = ref<number | null>(null);
const qrSessionId = ref('');
const qrCodeBase64 = ref('');
const authedSessionId = ref('');

const filingAgentUen = ref('');
const singpassId = ref('');
const password = ref('');
const otp = ref('');
const pwdSessionId = ref('');
const maskedMobile = ref('');

let pollTimer: ReturnType<typeof setInterval> | null = null;

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function close() {
  stopPolling();
  emit('update:modelValue', false);
}

function fail(msg: string) {
  errorMsg.value = msg;
  step.value = 'qr';
}

/** "I Understand" 클릭 → 로그인 방식 선택 화면으로. (아직 API 호출 없음) */
function onUnderstand() {
  errorMsg.value = '';
  step.value = 'method';
}

/** QR 방식 선택 → 여기서 request-qr(= MST 세션 시작). */
function chooseQr() {
  void startQr();
}

/** 비밀번호 방식 선택 → 아이디/비밀번호 화면. (MST 는 pwd-request 에서 시작) */
function choosePassword() {
  errorMsg.value = '';
  step.value = 'password';
}

async function startQr() {
  errorMsg.value = '';
  step.value = 'loading';
  try {
    const r = await sgPyScrapingApi.requestQr(props.taskId);
    mstIdx.value = r.mstIdx;
    if (!r.success || !r.sessionId) {
      errorMsg.value = r.error || 'Failed to generate QR.';
      step.value = 'method';
      return;
    }
    qrSessionId.value = r.sessionId;
    qrCodeBase64.value = r.qrCodeBase64 || '';
    step.value = 'qr';
    startPolling();
  } catch (e: any) {
    errorMsg.value = e?.message || 'Failed to start login.';
    step.value = 'method';
  }
}

/**
 * C# (SINGPASS.cs TTimer.SetTimeout(50)) 과 동일하게 50초 폴링 상한.
 * Singpass QR TTL(60초) 안(50s)에 스캔이 안 되면 만료 처리 → Refresh UI 전환.
 */
const POLL_LIMIT_MS = 50_000;
const POLL_INTERVAL_MS = 2500;
let pollStartAt = 0;

function markExpired() {
  stopPolling();
  step.value = 'qr_expired';
  errorMsg.value = '';
}

function startPolling() {
  stopPolling();
  pollStartAt = Date.now();
  pollTimer = setInterval(async () => {
    if (step.value !== 'qr' || !qrSessionId.value) return;
    // 클라이언트 자체 50초 상한 (서버 만료 응답과 별개)
    if (Date.now() - pollStartAt >= POLL_LIMIT_MS) {
      markExpired();
      return;
    }
    try {
      const s = await sgPyScrapingApi.qrStatus(qrSessionId.value);
      if (s.done) {
        stopPolling();
        await doQrAuth();
        return;
      }
      // 서버가 만료를 알려주면 즉시 만료 UI 로 전환 (Singpass session_expired)
      if (s.expired) {
        markExpired();
      }
    } catch {
      /* transient — keep polling */
    }
  }, POLL_INTERVAL_MS);
}

/** Refresh QR Code 버튼 → 새 QR 발급 후 폴링 재개. */
async function refreshQr() {
  stopPolling();
  await startQr();
}

async function doQrAuth() {
  if (!filingAgentUen.value.trim()) {
    fail('Filing Agent UEN is missing.');
    startPolling();
    return;
  }
  step.value = 'authenticating';
  try {
    const r = await sgPyScrapingApi.responseQr({
      sessionId: qrSessionId.value,
      filingAgentUEN: filingAgentUen.value.trim(),
      mstIdx: mstIdx.value,
    });
    if (r.success && r.authenticated && r.sessionId) {
      finish(r.sessionId);
    } else {
      fail(r.error || 'QR authentication failed.');
      startPolling();
    }
  } catch (e: any) {
    fail(e?.message || 'QR authentication failed.');
    startPolling();
  }
}

/** QR/비밀번호 화면에서 방식 선택 화면으로 돌아가기. */
function backToMethod() {
  stopPolling();
  errorMsg.value = '';
  step.value = 'method';
}

async function submitPassword() {
  if (!singpassId.value.trim() || !password.value) {
    errorMsg.value = 'Enter Singpass ID and password.';
    return;
  }
  errorMsg.value = '';
  step.value = 'authenticating';
  try {
    const r = await sgPyScrapingApi.pwdRequest({
      singpassId: singpassId.value.trim(),
      password: password.value,
      mstIdx: mstIdx.value,
      taskId: props.taskId,
    });
    if (r.success && r.sessionId) {
      mstIdx.value = r.mstIdx ?? mstIdx.value; // 비밀번호 방식은 여기서 MST 시작됨
      pwdSessionId.value = r.sessionId;
      maskedMobile.value = r.maskedMobileNumber || '';
      step.value = 'otp';
    } else {
      errorMsg.value = r.error || 'Password login failed.';
      step.value = 'password';
    }
  } catch (e: any) {
    errorMsg.value = e?.message || 'Password login failed.';
    step.value = 'password';
  }
}

async function submitOtp() {
  if (!otp.value.trim()) {
    errorMsg.value = 'Enter the OTP.';
    return;
  }
  if (!filingAgentUen.value.trim()) {
    errorMsg.value = 'Enter the Filing Agent UEN.';
    return;
  }
  errorMsg.value = '';
  step.value = 'authenticating';
  try {
    const r = await sgPyScrapingApi.pwdResponse({
      sessionId: pwdSessionId.value,
      otp: otp.value.trim(),
      filingAgentUEN: filingAgentUen.value.trim(),
      mstIdx: mstIdx.value,
    });
    if (r.success && r.authenticated && r.sessionId) {
      finish(r.sessionId);
    } else {
      errorMsg.value = r.error || 'OTP verification failed.';
      step.value = 'otp';
    }
  } catch (e: any) {
    errorMsg.value = e?.message || 'OTP verification failed.';
    step.value = 'otp';
  }
}

/** 인증 성공 → 바로 진행하지 않고 성공 화면 표시(사용자 Confirm 대기). */
function finish(sessionId: string) {
  stopPolling();
  authedSessionId.value = sessionId;
  errorMsg.value = '';
  step.value = 'success';
}

/** 성공 화면의 Confirm 클릭 → 실제 진행(authenticated emit). */
function confirmProceed() {
  emit('authenticated', {
    sessionId: authedSessionId.value,
    mstIdx: mstIdx.value,
    filingAgentUEN: filingAgentUen.value.trim(),
  });
  emit('update:modelValue', false);
}

function reset() {
  step.value = 'loading';
  errorMsg.value = '';
  mstIdx.value = null;
  qrSessionId.value = '';
  qrCodeBase64.value = '';
  authedSessionId.value = '';
  singpassId.value = '';
  password.value = '';
  otp.value = '';
  pwdSessionId.value = '';
  maskedMobile.value = '';
  filingAgentUen.value = props.filingAgentUEN || '';
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      // 열리면 먼저 "I Understand" 안내만 표시 — 확인을 눌러야 request-qr 호출 시작.
      reset();
      step.value = 'understand';
    } else {
      stopPolling();
    }
  },
);

onBeforeUnmount(stopPolling);
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="460"
    persistent
    @update:model-value="(v) => !v && close()"
  >
    <v-card rounded="lg" class="sgpy-login">
      <div class="sgpy-login__head">
        <span class="sgpy-login__title">Choose a login method</span>
        <v-btn icon="mdi-close" variant="text" size="small" @click="close" />
      </div>
      <v-divider />

      <v-card-text class="pa-5">
        <!-- understand gate: 확인을 눌러야 request-qr(API) 시작 -->
        <div v-if="step === 'understand'" class="sgpy-login__center">
          <v-icon color="warning" size="46">mdi-alert-circle-outline</v-icon>
          <div class="sgpy-login__understand">
            <div>{{ t('singpass.syncInProgressWarning1') || 'Please do NOT log in via Corppass in any services other than Lettaga,' }}</div>
            <div>{{ t('singpass.syncInProgressWarning2') || 'until you receive the sync completion email.' }}</div>
          </div>
          <v-btn block color="primary" variant="flat" class="text-none mt-3" @click="onUnderstand">
            {{ t('singpass.syncWarningConfirm') || 'I Understand' }}
          </v-btn>
        </div>

        <!-- loading -->
        <div v-else-if="step === 'loading'" class="sgpy-login__center">
          <v-progress-circular indeterminate color="primary" size="34" />
          <span>Generating QR…</span>
        </div>

        <!-- method: QR 또는 비밀번호 선택 (QR 생성 전) -->
        <template v-else-if="step === 'method'">
          <div class="sgpy-login__methodhint">How would you like to log in?</div>
          <v-btn
            block
            color="primary"
            variant="flat"
            size="large"
            class="text-none mb-3"
            prepend-icon="mdi-qrcode-scan"
            @click="chooseQr"
          >
            Login with Singpass QR
          </v-btn>
          <v-btn
            block
            color="primary"
            variant="tonal"
            size="large"
            class="text-none"
            prepend-icon="mdi-form-textbox-password"
            @click="choosePassword"
          >
            Login with password
          </v-btn>
        </template>

        <!-- qr: 스캔만 (선택한 방식만 표시) -->
        <template v-else-if="step === 'qr'">
          <div class="sgpy-login__qr">
            <img
              v-if="qrCodeBase64"
              :src="qrCodeBase64.startsWith('data:') ? qrCodeBase64 : `data:image/png;base64,${qrCodeBase64}`"
              alt="Singpass QR"
              class="sgpy-login__qr-img"
            />
            <div v-else class="sgpy-login__qr-missing">QR unavailable</div>
            <div class="sgpy-login__brand">singpass</div>
            <div class="sgpy-login__hint">Scan with the Singpass app, then it authenticates automatically.</div>
          </div>
          <v-btn variant="text" class="text-none mt-2" @click="backToMethod">&larr; Back</v-btn>
        </template>

        <!-- qr_expired: 만료 UI + Refresh 버튼 -->
        <template v-else-if="step === 'qr_expired'">
          <div class="sgpy-login__qr sgpy-login__qr--expired">
            <img
              v-if="qrCodeBase64"
              :src="qrCodeBase64.startsWith('data:') ? qrCodeBase64 : `data:image/png;base64,${qrCodeBase64}`"
              alt="Expired QR"
              class="sgpy-login__qr-img sgpy-login__qr-img--faded"
            />
            <div class="sgpy-login__qr-overlay">
              <div class="sgpy-login__qr-expired-title">QR code expired</div>
              <v-btn
                color="error"
                variant="flat"
                class="text-none"
                prepend-icon="mdi-refresh"
                @click="refreshQr"
              >
                Refresh QR Code
              </v-btn>
            </div>
            <div class="sgpy-login__brand">singpass</div>
          </div>
          <v-btn variant="text" class="text-none mt-2" @click="backToMethod">&larr; Back</v-btn>
        </template>

        <!-- password -->
        <template v-else-if="step === 'password'">
          <v-text-field
            v-model="singpassId"
            label="Singpass ID"
            density="compact"
            variant="outlined"
            hide-details
            class="mb-3"
          />
          <v-text-field
            v-model="password"
            label="Password"
            type="password"
            density="compact"
            variant="outlined"
            hide-details
            class="mb-4"
            @keyup.enter="submitPassword"
          />
          <div class="sgpy-login__actions">
            <v-btn variant="text" class="text-none" @click="backToMethod">Back</v-btn>
            <v-btn color="primary" variant="flat" class="text-none" @click="submitPassword">
              Send OTP
            </v-btn>
          </div>
        </template>

        <!-- otp -->
        <template v-else-if="step === 'otp'">
          <div class="sgpy-login__otp-info">
            OTP sent<span v-if="maskedMobile"> to {{ maskedMobile }}</span>.
          </div>
          <v-text-field
            v-model="otp"
            label="OTP (6 digits)"
            density="compact"
            variant="outlined"
            hide-details
            class="mb-4"
            @keyup.enter="submitOtp"
          />
          <div class="sgpy-login__actions">
            <v-btn variant="text" class="text-none" @click="step = 'password'">Back</v-btn>
            <v-btn color="primary" variant="flat" class="text-none" @click="submitOtp">Verify</v-btn>
          </div>
        </template>

        <!-- authenticating -->
        <div v-else-if="step === 'authenticating'" class="sgpy-login__center">
          <v-progress-circular indeterminate color="primary" size="34" />
          <span>Authenticating…</span>
        </div>

        <!-- success → confirm to proceed -->
        <div v-else-if="step === 'success'" class="sgpy-login__center">
          <v-icon color="success" size="46">mdi-check-circle</v-icon>
          <span class="sgpy-login__success">Login success</span>
          <div class="sgpy-login__success-hint">Click Confirm to proceed with the filing.</div>
          <v-btn
            block
            color="primary"
            variant="flat"
            class="text-none mt-3"
            @click="confirmProceed"
          >
            Confirm Complete
          </v-btn>
        </div>

        <v-alert
          v-if="errorMsg"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-4"
          :text="errorMsg"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.sgpy-login__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 10px 20px;
}
.sgpy-login__title {
  font-size: 15px;
  font-weight: 800;
  color: #1a2336;
}
.sgpy-login__center {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 28px 0;
  color: #6b7a94;
  font-size: 13px;
}
.sgpy-login__qr {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 6px 0 12px;
}
.sgpy-login__qr-img {
  width: 210px;
  height: 210px;
  border: 3px solid #ef4444;
  border-radius: 14px;
  padding: 8px;
  object-fit: contain;
}
/* 만료 UI: 흐릿한 QR 위에 "QR code expired" + Refresh 버튼 오버레이 */
.sgpy-login__qr--expired { position: relative; }
.sgpy-login__qr-img--faded { opacity: 0.25; filter: grayscale(1); }
.sgpy-login__qr-overlay {
  position: absolute;
  top: 6px; left: 0; right: 0;
  width: 210px; height: 210px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.sgpy-login__qr-expired-title {
  font-weight: 800;
  font-size: 15px;
  color: #111827;
}
.sgpy-login__qr-missing {
  width: 210px;
  height: 210px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #d5dae4;
  border-radius: 14px;
  color: #8a95a8;
  font-size: 13px;
}
.sgpy-login__brand {
  font-weight: 800;
  color: #ef4444;
  letter-spacing: 0.5px;
}
.sgpy-login__hint {
  font-size: 12px;
  color: #8a95a8;
  text-align: center;
  max-width: 300px;
}
.sgpy-login__stars {
  color: #ef4444;
  font-weight: 800;
  margin-right: 6px;
  letter-spacing: 1px;
}
.sgpy-login__understand {
  font-size: 13px;
  color: #42506a;
  line-height: 1.7;
  text-align: center;
  max-width: 340px;
}
.sgpy-login__success {
  font-size: 15px;
  font-weight: 800;
  color: #16a34a;
}
.sgpy-login__success-hint {
  font-size: 12.5px;
  color: #6b7a94;
}
.sgpy-login__methodhint {
  font-size: 13px;
  font-weight: 700;
  color: #42506a;
  text-align: center;
  margin-bottom: 16px;
}
.sgpy-login__otp-info {
  font-size: 12.5px;
  color: #42506a;
  margin-bottom: 10px;
}
.sgpy-login__actions {
  display: flex;
  justify-content: space-between;
}
</style>
